import { useState } from "react";
import type { FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resetPassword } from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import logo from "@/assets/famibelleza_logo.png";

export default function ResetPassword() {
  const { t } = useTranslation();
  usePageTitle(t("resetPassword.title"));
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!token) {
    return (
      <div className="relative flex items-center justify-center py-12 px-4 overflow-hidden">
        <img src={logo} alt="" className="absolute top-0 w-1/2 h-full object-contain z-0 opacity-10 blur-md" />
        <div className="relative z-10 max-w-md w-full bg-white rounded-2xl shadow-md p-8 text-center space-y-4">
          <h1 className="text-3xl font-extrabold text-brand">{t("resetPassword.invalidTitle")}</h1>
          <p className="text-gray-600">{t("resetPassword.invalidDescription")}</p>
          <Link to="/forgot-password" className="text-sm font-medium text-brand hover:text-brand/80">
            {t("resetPassword.requestNewLink")}
          </Link>
        </div>
      </div>
    );
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirmPassword) {
      setError(t("resetPassword.passwordsMismatch"));
      return;
    }

    setSubmitting(true);
    try {
      await resetPassword(token, password);
      navigate("/login");
    } catch (err) {
      setError(err instanceof Error ? err.message : t("resetPassword.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative flex items-center justify-center py-12 px-4 overflow-hidden">
      <img src={logo} alt="" className="absolute top-0 w-1/2 h-full object-contain z-0 opacity-10 blur-md" />
      <div className="relative z-10 max-w-md w-full bg-white rounded-2xl shadow-md p-8 space-y-6">
        <h1 className="text-center text-3xl font-extrabold text-brand">{t("resetPassword.title")}</h1>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label htmlFor="password" className="text-sm font-medium text-gray-700">{t("resetPassword.newPassword")}</label>
            <input
              id="password"
              type="password"
              required
              minLength={10}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
              value={password}
              onChange={e => setPassword(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">{t("common.confirmPassword")}</label>
            <input
              id="confirmPassword"
              type="password"
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand focus:border-brand"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
            />
          </div>

          {error && (
            <div className="rounded-md bg-red-50 p-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 px-4 bg-brand text-white font-semibold rounded-2xl cursor-pointer hover:bg-brand/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {submitting ? t("resetPassword.submitting") : t("resetPassword.submit")}
          </button>
        </form>
      </div>
    </div>
  );
}
